import React, { useState } from 'react';
import { X, Building2, Calendar, DollarSign, Target, Settings, CheckCircle2, CreditCard as Edit3, RefreshCw } from 'lucide-react';
import { Promocao, PromocaoSelecionada } from '../types/multipatrocinadores';
import { SegmentacaoModal } from './SegmentacaoModal';
import { ConfirmacaoModal } from './ConfirmacaoModal';
import { AlterarSegmentacaoModal } from './AlterarSegmentacaoModal';
import { multipatrocinadorService } from '../services/multipatrocinadores';

interface PromocaoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess?: () => void;
  promocao: Promocao | null;
  promocaoSelecionada?: PromocaoSelecionada;
  onSave?: (configuracao: PromocaoSelecionada) => void;
}

export const PromocaoModal: React.FC<PromocaoModalProps> = ({
  isOpen,
  onClose,
  onSaveSuccess,
  promocao,
  promocaoSelecionada,
  onSave,
}) => {
  const [showSegmentacaoModal, setShowSegmentacaoModal] = useState(false);
  const [showConfirmacaoModal, setShowConfirmacaoModal] = useState(false);
  const [showAlterarSegmentacaoModal, setShowAlterarSegmentacaoModal] = useState(false);
  const [allowSegmentacaoChange, setAllowSegmentacaoChange] = useState(false);
  const [localConfig, setLocalConfig] = useState<PromocaoSelecionada | null>(null);
  const [hasEANs, setHasEANs] = useState<boolean>(false);
  const [checkingEANs, setCheckingEANs] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  React.useEffect(() => {
    if (promocao) {
      const budgetAtual = parseFloat(promocao.OrcamentoPromocao || '0');

      // Verificar se a promoção já foi editada (tem campos extras)
      const jaEditada = !!(promocao as any).novoBudget !== undefined || !!(promocao as any).tipoSegmentacao;

      if (jaEditada) {
        // Carregar configuração existente dos dados da promoção
        setLocalConfig({
          promocao,
          budgetPlataforma: (promocao as any).budgetPlataforma || budgetAtual,
          novoBudget: (promocao as any).novoBudget || budgetAtual,
          confirmarBudget: (promocao as any).confirmarBudget || false,
          segmentacao: (promocao as any).segmentacao || {
            tipo: 'IDRegra',
            configuracoes: []
          }
        });
      } else if (promocaoSelecionada) {
        // Usar configuração passada como prop
        setLocalConfig(promocaoSelecionada);
      } else {
        // Criar configuração inicial
        setLocalConfig({
          promocao,
          budgetPlataforma: budgetAtual,
          novoBudget: budgetAtual,
          confirmarBudget: false,
          segmentacao: {
            tipo: 'IDRegra',
            configuracoes: []
          }
        });
      }
    }

    // Verificar se há EANs disponíveis para esta promoção
    if (promocao) {
      checkEANsAvailability();
    }

    // Limpar mensagens ao abrir modal
    setSaveMessage(null);

    // Resetar flag de alteração de segmentação
    setAllowSegmentacaoChange(false);
  }, [promocao, promocaoSelecionada]);



  const checkEANsAvailability = async () => {
    if (!promocao) return;
    
    setCheckingEANs(true);
    try {
      const response = await multipatrocinadorService.getEANs(promocao.IDRegraPromocao);
      if (response.success && response.data.success && response.data.data.length > 0) {
        setHasEANs(true);
      } else {
        setHasEANs(false);
      }
    } catch (error) {
      console.error('Erro ao verificar EANs:', error);
      setHasEANs(false);
    } finally {
      setCheckingEANs(false);
    }
  };

  if (!isOpen || !promocao || !localConfig) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case '1':
        return { bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' };
      case '0':
        return { bg: 'bg-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-500' };
      case '2':
        return { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' };
      default:
        return { bg: 'bg-gray-100', text: 'text-gray-700', dot: 'bg-gray-500' };
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case '1': return 'Ativa';
      case '0': return 'Pausada';
      case '2': return 'Finalizada';
      default: return 'Desconhecido';
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR');
    } catch {
      return dateString;
    }
  };

  const statusInfo = getStatusColor(promocao.Status);
  const budgetAtual = parseFloat(promocao.OrcamentoPromocao || '0');
  const budgetSaldo = parseFloat(promocao.OrcamentoSaldo || '0');
  const budgetConsumido = parseFloat(promocao.OrcamentoConsumido || '0');

  const handleBudgetChange = (field: keyof PromocaoSelecionada, value: any) => {
    setLocalConfig(prev => prev ? { ...prev, [field]: value } : null);
  };

  const handleSegmentacaoChange = (segmentacao: any) => {
    setLocalConfig(prev => prev ? { ...prev, segmentacao } : null);
  };

  const handleAlterarSegmentacaoClick = () => {
    setShowAlterarSegmentacaoModal(true);
  };

  const handleConfirmarAlteracaoSegmentacao = () => {
    setShowAlterarSegmentacaoModal(false);
    setAllowSegmentacaoChange(true);
  };

  const handleSegmentacaoDropdownChange = (novoTipo: 'IDRegra' | 'BU' | 'Marca' | 'EAN') => {
    handleSegmentacaoChange({
      tipo: novoTipo,
      configuracoes: []
    });
    setAllowSegmentacaoChange(false);
  };

  const handleSaveClick = () => {
    setShowConfirmacaoModal(true);
  };

  const handleConfirmarSalvar = async () => {
    if (!localConfig) return;

    setShowConfirmacaoModal(false);
    setIsSaving(true);
    setSaveMessage(null);

    try {
      // Preparar payload para o endpoint
      const payload = {
        idRegraPromocao: promocao.IDRegraPromocao,
        nomePromocao: promocao.Nome,
        budgetPlataforma: localConfig.budgetPlataforma,
        novoBudget: localConfig.novoBudget,
        confirmarBudget: localConfig.confirmarBudget,
        tipoSegmentacao: localConfig.segmentacao.tipo,
        segmentacao: {
          tipo: localConfig.segmentacao.tipo,
          configuracoes: localConfig.segmentacao.configuracoes.map(config => ({
            id: config.id,
            nome: config.nome,
            budget: config.budget,
            multipatrocinadores: {
              coluna1: {
                tipo: config.multipatrocinadores.coluna1.tipo,
                porcentagem: config.multipatrocinadores.coluna1.porcentagem
              },
              coluna2: {
                tipo: config.multipatrocinadores.coluna2.tipo,
                porcentagem: config.multipatrocinadores.coluna2.porcentagem
              },
              coluna3: {
                tipo: config.multipatrocinadores.coluna3.tipo,
                porcentagem: config.multipatrocinadores.coluna3.porcentagem
              }
            }
          }))
        }
      };
      
      const response = await multipatrocinadorService.saveConfiguration(promocao.IDRegraPromocao, payload);
      
      if (response.success) {
        setSaveMessage({
          type: 'success',
          message: 'Configuração salva com sucesso!'
        });
        
        // Chamar callback se existir (para atualizar lista local)
        if (onSave) {
          onSave(localConfig);
        }
        
        // Chamar callback de sucesso para refresh
        if (onSaveSuccess) {
          onSaveSuccess();
        }
        
        // Fechar modal após 2 segundos
        setTimeout(() => {
          onClose();
          setSaveMessage(null);
        }, 2000);
      } else {
        setSaveMessage({
          type: 'error',
          message: response.error || 'Erro ao salvar configuração'
        });
      }
    } catch (error) {
      setSaveMessage({
        type: 'error',
        message: 'Erro de conexão ao salvar configuração'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSegmentacaoSave = (configuracoes: any[]) => {
    if (localConfig) {
      const newSegmentacao = {
        ...localConfig.segmentacao,
        configuracoes
      };
      handleSegmentacaoChange(newSegmentacao);
    }
    setShowSegmentacaoModal(false);
  };

  const isPromocaoModified = () => {
    return !!(promocao as any).modified || (localConfig?.segmentacao.configuracoes.length ?? 0) > 0;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{zIndex: 10000}}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="relative overflow-hidden">
          <div className="flex items-center justify-between p-6 bg-blue-600 text-white">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-white bg-opacity-20 rounded-xl backdrop-blur-sm">
                <Target className="h-7 w-7" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">{promocao.Nome}</h2>
                <p className="text-blue-100 mt-1">ID: {promocao.IDRegraPromocao}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={isSaving}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors disabled:opacity-50"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-8">
            {/* Informações Básicas */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Building2 className="h-5 w-5 mr-2 text-blue-600" />
                Informações da Promoção
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <div className={`inline-flex items-center px-3 py-2 rounded-full text-sm font-medium ${statusInfo.bg} ${statusInfo.text}`}>
                      <div className={`w-2 h-2 rounded-full ${statusInfo.dot} mr-2`}></div>
                      {getStatusLabel(promocao.Status)}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Agrupamento</label>
                    <div className="bg-orange-100 text-orange-700 px-3 py-2 rounded-lg text-sm font-medium inline-block">
                      {promocao.AgrupamentoPromocao}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Benefício</label>
                    <div className="bg-green-100 text-green-700 px-3 py-2 rounded-lg text-sm font-medium inline-block">
                      {parseFloat(promocao.BeneficioValor).toFixed(2)}%
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Período</label>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <Calendar className="h-4 w-4" />
                      <span>{formatDate(promocao.DataHoraInicio)} - {formatDate(promocao.DataHoraFim)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Budget Original */}
              {(budgetAtual > 0 || budgetSaldo > 0 || budgetConsumido > 0) && (
                <div className="mt-6 bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <h4 className="text-sm font-medium text-blue-700 mb-3">Orçamento Original</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Total:</span>
                      <div className="font-medium text-gray-900">{formatCurrency(budgetAtual)}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Saldo:</span>
                      <div className="font-medium text-green-600">{formatCurrency(budgetSaldo)}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Consumido:</span>
                      <div className="font-medium text-red-600">{formatCurrency(budgetConsumido)}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Configurações */}
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                <Settings className="h-5 w-5 mr-2 text-blue-600" />
                Configurações
              </h3>

              <div className="space-y-6">
                {/* Novo Budget */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <div className="flex items-center space-x-2">
                      <DollarSign className="h-4 w-4 text-blue-600" />
                      <span>Novo Budget</span>
                    </div>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-3 text-gray-500">R$</span>
                    <input
                      type="number"
                      value={localConfig.novoBudget}
                      onChange={(e) => handleBudgetChange('novoBudget', parseFloat(e.target.value) || 0)}
                      disabled={isSaving}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder="0,00"
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>

                {/* Segmentação */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <div className="flex items-center space-x-2">
                      <Target className="h-4 w-4 text-orange-600" />
                      <span>Segmentação</span>
                    </div>
                  </label>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Tipo de Segmentação</label>
                      {isPromocaoModified() && !allowSegmentacaoChange ? (
                        <button
                          onClick={handleAlterarSegmentacaoClick}
                          disabled={isSaving}
                          className="w-full flex items-center justify-center space-x-2 px-3 py-2 border border-orange-300 bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <RefreshCw className="h-4 w-4" />
                          <span>Alterar segmentação atual ({localConfig.segmentacao.tipo})</span>
                        </button>
                      ) : (
                        <select
                          value={localConfig.segmentacao.tipo}
                          onChange={(e) => handleSegmentacaoDropdownChange(e.target.value as 'IDRegra' | 'BU' | 'Marca' | 'EAN')}
                          disabled={isSaving}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <option value="IDRegra">ID Regra</option>
                          <option value="BU">BU</option>
                          {hasEANs && <option value="EAN">EAN</option>}
                        </select>
                      )}
                      {checkingEANs && (
                        <div className="flex items-center space-x-2 mt-1 text-xs text-blue-600">
                          <div className="w-3 h-3 border border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                          <span>Verificando EANs disponíveis...</span>
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Configurações</label>
                      <button
                        onClick={() => setShowSegmentacaoModal(true)}
                        disabled={isSaving}
                        className="w-full flex items-center justify-center space-x-2 px-3 py-2 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Edit3 className="h-4 w-4" />
                        <span>
                          {localConfig.segmentacao.configuracoes.length > 0 
                            ? `${localConfig.segmentacao.configuracoes.length} itens configurados`
                            : 'Clique para configurar'
                          }
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Mensagem de Status */}
            {saveMessage && (
              <div className={`p-4 rounded-lg border-2 ${
                saveMessage.type === 'success'
                  ? 'bg-green-50 border-green-200 text-green-700'
                  : 'bg-red-50 border-red-200 text-red-700'
              }`}>
                <div className="flex items-center space-x-2">
                  {saveMessage.type === 'success' ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  ) : (
                    <X className="h-5 w-5 text-red-600" />
                  )}
                  <span className="font-medium">{saveMessage.message}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            <span className="font-medium">
              Diferença: {formatCurrency(localConfig.novoBudget - localConfig.budgetPlataforma)}
            </span>
            <span className="ml-4">
              {localConfig.segmentacao.tipo}: 
              {localConfig.segmentacao.configuracoes.length > 0 
                ? ` ${localConfig.segmentacao.configuracoes.length} configurados`
                : ' Não configurado'
              }
            </span>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancelar
            </button>
            <button
              onClick={handleSaveClick}
              disabled={isSaving}
              className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Salvando...</span>
                </>
              ) : (
                <span>Salvar Configuração</span>
              )}
            </button>
          </div>
        </div>

        {/* Modal de Segmentação */}
        
        <SegmentacaoModal
          isOpen={showSegmentacaoModal}
          onClose={() => setShowSegmentacaoModal(false)}
          onSave={handleSegmentacaoSave}
          tipo={localConfig.segmentacao.tipo}
          configuracoes={localConfig.segmentacao.configuracoes}
          promocaoId={promocao.IDRegraPromocao}
          novoBudget={localConfig.novoBudget}
        />

        {/* Modal de Confirmação */}
        <ConfirmacaoModal
          isOpen={showConfirmacaoModal}
          onClose={() => setShowConfirmacaoModal(false)}
          onConfirm={handleConfirmarSalvar}
          isLoading={isSaving}
        />

        {/* Modal de Alteração de Segmentação */}
        <AlterarSegmentacaoModal
          isOpen={showAlterarSegmentacaoModal}
          onClose={() => setShowAlterarSegmentacaoModal(false)}
          onConfirm={handleConfirmarAlteracaoSegmentacao}
          tipoAtual={localConfig.segmentacao.tipo}
        />
      </div>
    </div>
  );
};